// ERA Motor — Offline Mesaj Kuyruğu (Tur 2.5B)
// IndexedDB tabanlı, kullanıcı hesabına izole, sekme kapansa da kalıcı.
//
// Kural:
//  - Her mesajda `client_message_id` (UUID) bulunur — sunucu bunu idempotency
//    olarak kullanır, aynı mesaj iki kez oluşmaz.
//  - Kuyruk `user_id` ile izole edilir: logout → kullanıcı bazlı temizleme.
//  - Token, JWT veya subscription key IndexedDB'de SAKLANMAZ.

const DB_NAME = "era-motor-offline-v1";
const DB_VERSION = 1;
const STORE = "pending_messages";

function _openDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB desteklenmiyor"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: "id" });
        os.createIndex("by_user", "user_id", { unique: false });
        os.createIndex("by_conversation", ["user_id", "conversation_id"], { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("IndexedDB açılamadı"));
  });
}

function _uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // basit fallback
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

async function _tx(mode, fn) {
  const db = await _openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    let result;
    try { result = fn(store); } catch (e) { reject(e); return; }
    tx.oncomplete = () => { db.close(); resolve(result); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error); };
  });
}

/** Kuyruğa yeni offline mesaj ekle.
 *  @param {Object} params  { userId, conversationType: 'service'|'general', conversationId, body, photo_base64? }
 *  @returns {Promise<Object>} kaydedilen queue item
 */
export async function enqueueMessage({ userId, conversationType, conversationId, body = "", photo_base64 = "" }) {
  if (!userId || !conversationId) throw new Error("userId + conversationId gerekli");
  const item = {
    id: _uuid(),                          // internal queue id
    client_message_id: _uuid(),           // backend idempotency key
    user_id: userId,
    conversation_type: conversationType,
    conversation_id: conversationId,
    body,
    photo_base64,
    status: "pending",                    // pending | sending | failed
    attempt: 0,
    last_error: "",
    created_at: new Date().toISOString(),
  };
  await _tx("readwrite", (s) => s.add(item));
  return item;
}

/** Kullanıcıya + konuşmaya ait bekleyen mesajları listeler (kronolojik). */
export async function listPending(userId, conversationId = null) {
  const items = await _tx("readonly", (s) => {
    return new Promise((resolve, reject) => {
      const out = [];
      const idx = conversationId ? s.index("by_conversation") : s.index("by_user");
      const key = conversationId ? [userId, conversationId] : userId;
      const req = idx.openCursor(IDBKeyRange.only(key));
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) { out.push(cursor.value); cursor.continue(); }
        else resolve(out);
      };
      req.onerror = () => reject(req.error);
    });
  });
  return items.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
}

/** Kuyruk item'ini güncelle (status/attempt/last_error). */
export async function updateItem(id, patch) {
  await _tx("readwrite", (s) => {
    const req = s.get(id);
    req.onsuccess = () => {
      const cur = req.result;
      if (!cur) return;
      Object.assign(cur, patch);
      s.put(cur);
    };
  });
}

/** Item'i sil. */
export async function removeItem(id) {
  await _tx("readwrite", (s) => s.delete(id));
}

/** Bir kullanıcının tüm bekleyen mesajlarını temizle (logout için). */
export async function clearUser(userId) {
  await _tx("readwrite", (s) => {
    const req = s.index("by_user").openCursor(IDBKeyRange.only(userId));
    req.onsuccess = () => {
      const cur = req.result;
      if (cur) { s.delete(cur.primaryKey); cur.continue(); }
    };
  });
}
